/**
 * Block 12 PR-C Phase C7 Step 3 — FreeAnketaFormView (Presenter).
 *
 * Свободный режим UI per ADR-0020 §Q9 + методика §2.8. Структура:
 *   Header — sticky title «Другая мебель» (hotfix-2A Phase A3 Step 6, Bug 19)
 *   Section А — Параметры (projectName text input)
 *   Section Б — Позиции сметы (FreeRowsSection editable rows)
 *   Section И — Итог (total + Save CTA + optional КП CTA)
 *
 * Layout mirror AnketaFormView (Header at top, flex column gap-6 sections).
 * BottomBar wrap'ится router'ом / App layout (TODO: Phase A4 wires BottomBar
 * 3-button variant — Audit Round 2 Bug 5 / P-036). Pure presenter — handlers
 * controlled через `actions` / `onSave` / `onCreateQuote`.
 *
 * Reference: ADR-0020 §Q9, методика §2.8.
 */

import type { FreeAnketaActions, FreeAnketaState } from './types';
import { FreeRowsSection } from './FreeRowsSection';
import { TextInput } from '@/components/forms/TextInput';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Header } from '@/components/Header/Header';
import { ANKETA_TYPE_LABELS } from '../anketa/anketaTypeLabels';
import { DisclaimerBanner } from '../anketa/components/DisclaimerBanner';

export interface FreeAnketaFormViewProps {
  state: FreeAnketaState;
  total: number;
  isValid: boolean;
  actions: FreeAnketaActions;
  isSaving: boolean;
  onSave: () => Promise<void> | void;
  onCreateQuote?: () => void;
}

export function FreeAnketaFormView({
  state,
  total,
  isValid,
  actions,
  isSaving,
  onSave,
  onCreateQuote,
}: FreeAnketaFormViewProps) {
  const saveDisabled = !isValid || isSaving;

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={ANKETA_TYPE_LABELS.other} />
      <div className="flex-1 overflow-y-auto pb-6 flex flex-col gap-6 p-3">
        <DisclaimerBanner />
        <section
          className="flex flex-col gap-3"
          aria-labelledby="free-params-heading"
          data-testid="free-section-params"
        >
          <h3 id="free-params-heading" className="text-ink font-bold text-lg">
            Параметры
          </h3>
          <TextInput
            name="projectName"
            label="Название проекта"
            value={state.projectName}
            onChange={actions.setProjectName}
            placeholder="Например, Барная стойка для гостиной"
            maxLength={200}
            required
          />
        </section>

        <FreeRowsSection
          rows={state.freeRows}
          onAdd={actions.addRow}
          onUpdate={actions.updateRow}
          onRemove={actions.removeRow}
        />

        <section
          className="flex flex-col gap-4 rounded-2xl bg-card border border-accent p-5"
          aria-labelledby="free-total-heading"
          data-testid="free-section-total"
        >
          <h3 id="free-total-heading" className="text-ink font-bold text-lg">
            Итог
          </h3>
          <div className="text-center">
            <div className="text-muted text-sm mb-1">Общая сумма</div>
            <div
              className="text-ink font-bold text-4xl"
              aria-live="polite"
              data-testid="free-total-amount"
            >
              {total.toLocaleString('ru-RU')} ₸
            </div>
          </div>

          {!isValid ? (
            <p role="alert" className="text-error text-sm text-center">
              {state.projectName.trim() === ''
                ? 'Укажите название проекта'
                : state.freeRows.length === 0
                  ? 'Добавьте хотя бы одну позицию'
                  : 'Заполните название и стоимость в каждой позиции (количество > 0)'}
            </p>
          ) : null}

          {/* Hotfix-2A Phase A5 Step 10: Save CTAs match SectionTotal styling
              (consistency между typed + free anketa per прототип 2.12.{2,9}). */}
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={saveDisabled}
            data-testid="free-save"
            aria-label="Сохранить и продолжить"
            aria-disabled={saveDisabled}
            className={
              'w-full rounded-[14px] border-[1.5px] border-accent bg-transparent text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 ' +
              (saveDisabled ? 'opacity-50 cursor-not-allowed' : '')
            }
            style={{ minHeight: '56px', fontSize: '22px', fontWeight: 500 }}
          >
            {isSaving ? 'Сохраняем...' : 'Сохранить и продолжить'}
          </button>

          {onCreateQuote ? (
            <button
              type="button"
              onClick={onCreateQuote}
              data-testid="free-quote"
              aria-label="Сохранить и сформировать коммерческое предложение"
              className="w-full rounded-[14px] bg-cta border-b-2 border-b-cta-shadow text-ink font-display focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
              style={{
                minHeight: '120px',
                fontSize: '26px',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              Сохранить и сделать коммерческое предложение
            </button>
          ) : null}
        </section>
      </div>
      <BottomBar variant="default" />
    </main>
  );
}
